/**
 * Chart of Account IDs — used for auto-journaling.
 * These UUIDs match the chart_of_accounts table in Supabase.
 */
import { supabase } from './supabase';

export const ACCOUNTS = {
  // ASSETS
  CASH_LKR:           '0179d986-d99d-4862-887f-6a9c50097051', // 1000
  CASH_USD:           '7be9ff06-3b4f-43b5-bb3f-2e39ff1d7802', // 1001
  CASH_AED:           '06efdcc5-7b68-432a-8910-6335d63429ad', // 1002
  CHEQUES_IN_HAND:    'ebe40e7c-de6d-4f76-8ea3-dab8a9df14e6', // 1005
  BANK_LKR:           '0c290d7e-e2f4-4f3e-be1e-b99118a43287', // 1010
  BANK_USD:           '2e41de5a-f1cb-4623-8e35-f035400418b3', // 1011
  BANK_AED:           'b0a07c42-09aa-425e-8b7d-f868e9eaa10a', // 1012
  PETTY_CASH:         '2dc08a07-1798-438e-9aba-d0d6d27c4560', // 1020
  AR:                 'a14ec06a-89b9-4959-bdb0-fec820770a84', // 1030

  // LIABILITIES (payables)
  AP_SUPPLIERS:       'cae04da3-8e7a-41ff-8494-595b16a00579', // 2000
  CLIENT_DEPOSITS:    '73314be5-c07a-4a65-b74a-ed130ee994b6', // 2010
  SUSPENSE:           'd7317e1d-5d01-41a7-8270-b1afda175120', // 2015 — unidentified deposits, pending reclassification
  VISA_PAYABLE:       '09390dab-96bd-4685-b0dc-1c1f32a34bca', // 2020
  AIRLINE_PAYABLE:    '15f74bca-190f-471f-bff7-851c35e53732', // 2030
  HOTEL_LAND_PAYABLE: 'd3591694-78b7-4b7d-b5bb-8269de0c6b70', // 2040
  OTHER_PAYABLES:         'e07886aa-ec27-4b2b-b434-d1bd856286e4', // 2060
  COMMISSION_PAYABLE:     'c97ff2fc-7270-4241-a1d1-0f54cb8882d7', // 2070

  // EQUITY
  OPENING_BALANCE_EQUITY: 'b1c2d3e4-f5a6-7890-abcd-ef1234567890', // 3005

  // INCOME
  PACKAGE_REVENUE_LKR: 'e5a237f8-a4c9-4801-ae81-ed86fe59311e', // 4000
  TICKET_REVENUE:     '0aed9fff-2b6c-4fd0-994e-ed8eb1340e54', // 4010
  VISA_REVENUE:       'f3ddc38f-ac81-478f-a1ea-81829557e6c2', // 4020
  BOOKING_REVENUE:    '03f91276-500a-425d-af9f-1325eda994c3', // 4030 (Hotel & Land Package Revenue)
  FX_GAIN:            '26049eab-1e10-4b3a-b5fa-cbe4bb9df7cd', // 4050
  OTHER_INCOME:       '40df1b65-b111-4706-8ff5-09315010c1fc', // 4060

  // EXPENSES
  TICKET_COST_USD:    'f26caa71-cd3b-441f-87e8-920d58ebb5d3', // 5000
  TICKET_COST_LKR:    '3b931fa2-06f3-4617-9bbd-bd865085e03e', // 5001
  HOTEL_LAND_COST:    '7561eb33-d500-49c0-88c1-c202d20eae96', // 5010
  VISA_COST:          '270fb9f4-6d07-414a-8893-0c0901942392', // 5020
  PACKAGE_COST_USD:   '84a8a915-0349-4619-9d81-83e892968c19', // 5030
  PACKAGE_COST_LKR:   '5bf5bf1a-c2d7-42b7-807e-16a2dcee572c', // 5031
  FX_LOSS:            'f182afd0-8567-49e2-bad0-7f8f40746e55', // 5050
  NRD_LOSS:           'b437db47-46e5-43bc-91bb-5a1e0cfabf6a', // 5060
  COMMISSION_EXPENSE: '5eefb339-a66e-4ea8-b88d-b6b81a7c1306', // 6120

  // ASSET - prepayments
  SUPPLIER_DEPOSITS:  '3398ae17-c0fe-4e42-8421-0a508342ef01', // 1040

  FORFEITED_CLIENT_DEPOSITS: 'ca8c678f-a747-4f2c-bf5a-45dbb9be058e', // 4070
};

/**
 * Map receipt payment_mode → debit account_id (which bank/cash account receives the money)
 */
export function paymentModeToAccount(payment_mode) {
  switch ((payment_mode || '').toUpperCase()) {
    case 'BANK TRANSFER':    return ACCOUNTS.BANK_LKR;
    case 'CASH DEPOSIT':     return ACCOUNTS.BANK_LKR;
    case 'COMMERCIAL BANK':  return ACCOUNTS.BANK_LKR;
    case 'CARD':             return ACCOUNTS.BANK_LKR;
    case 'CHEQUE':           return ACCOUNTS.CHEQUES_IN_HAND;
    case 'CASH':             return ACCOUNTS.CASH_LKR;
    default:                 return ACCOUNTS.BANK_LKR;
  }
}

/**
 * Base currency is LKR. Returns the FX rate to apply to convert a document
 * amount to LKR: 1 for LKR, otherwise the rate entered on the document.
 */
export function rateFor(currencyCode, docRate) {
  if (!currencyCode || currencyCode.toUpperCase() === 'LKR') return 1;
  return parseFloat(docRate) || 1;
}

/**
 * Convert a document amount to base currency (LKR).
 */
export function baseAmount(amount, currencyCode, docRate) {
  return parseFloat(amount || 0) * rateFor(currencyCode, docRate);
}

/**
 * For a product type and the cost currency, return the cost (expense) account
 * and the supplier payable account to use when journaling cost at point of sale.
 *   productType: 'Ticket' | 'Visa' | 'Package'
 */
export function costAccountsFor(productType, costCurrencyCode) {
  const isLKR = !costCurrencyCode || costCurrencyCode.toUpperCase() === 'LKR';
  switch (productType) {
    case 'Ticket':
      return { expenseAccount: isLKR ? ACCOUNTS.TICKET_COST_LKR : ACCOUNTS.TICKET_COST_USD, payableAccount: ACCOUNTS.AIRLINE_PAYABLE };
    case 'Visa':
      return { expenseAccount: ACCOUNTS.VISA_COST, payableAccount: ACCOUNTS.VISA_PAYABLE };
    case 'Package':
    default:
      return { expenseAccount: isLKR ? ACCOUNTS.PACKAGE_COST_LKR : ACCOUNTS.PACKAGE_COST_USD, payableAccount: ACCOUNTS.HOTEL_LAND_PAYABLE };
  }
}

/**
 * Cost/expense account for a supplier cost line, by supplier type and currency.
 */
export function cogsAccountForSupplierType(supplierType, currencyCode) {
  const isUSD = currencyCode && currencyCode.toUpperCase() === 'USD';
  switch ((supplierType || '').toLowerCase()) {
    case 'airline': return isUSD ? ACCOUNTS.TICKET_COST_USD : ACCOUNTS.TICKET_COST_LKR;
    case 'hotel':
    case 'land operator':
    case 'land':
    case 'dmc': return ACCOUNTS.HOTEL_LAND_COST;
    case 'visa':
    case 'visa agent':
    case 'embassy': return ACCOUNTS.VISA_COST;
    default: return isUSD ? ACCOUNTS.PACKAGE_COST_USD : ACCOUNTS.PACKAGE_COST_LKR;
  }
}

/**
 * How much of a supplier's prepaid advance (Supplier Deposits, tagged to this exact
 * supplier) is still unconsumed for a specific package. Origination = the "advance
 * paid" entry posted when the deposit is added (reference_type='package'). Consumption
 * = prior per-booking offset entries (reference_type='BOOKING_COST' or
 * 'BOOKING_NRD_OFFSET') for any of this package's bookings. Scoped to the package so
 * different packages' advances to the same supplier never bleed into each other.
 */
export async function getSupplierDepositRemaining(packageId, supplierId) {
  if (!packageId || !supplierId) return 0;
  const { data: pkgBookings } = await supabase.from('bookings').select('id').eq('package_id', packageId);
  const bookingIds = (pkgBookings || []).map(b => b.id);
  const { data: lines } = await supabase.from('journal_lines')
    .select('debit_amount, credit_amount, journal_entries!inner(reference_type, reference_id, status)')
    .eq('account_id', ACCOUNTS.SUPPLIER_DEPOSITS)
    .eq('party_type', 'supplier').eq('party_id', supplierId)
    .eq('journal_entries.status', 'POSTED');
  return (lines || [])
    .filter(l => {
      const je = l.journal_entries;
      return (je.reference_type === 'package' && je.reference_id === packageId) ||
             (['BOOKING_COST', 'BOOKING_NRD_OFFSET'].includes(je.reference_type) && bookingIds.includes(je.reference_id));
    })
    .reduce((s, l) => s + parseFloat(l.debit_amount || 0) - parseFloat(l.credit_amount || 0), 0);
}

/**
 * Payable account for an invoice cost row: the supplier's own type decides it (an airline
 * on a package invoice is still Airline Ticket Payable), matching where payment vouchers
 * clear it. Falls back to the invoice-type default only when the supplier's type doesn't
 * map to a specific payable account.
 */
export function costPayableAccount(supplierType, fallback) {
  if (!supplierType) return fallback;
  const acct = payableAccountForSupplierType(supplierType);
  return acct === ACCOUNTS.AP_SUPPLIERS ? fallback : acct;
}

// { supplier_id: supplier_type } for the given ids.
export async function supplierTypesFor(ids) {
  const uniq = [...new Set((ids || []).filter(Boolean))];
  if (!uniq.length) return {};
  const { data } = await supabase.from('suppliers').select('id, supplier_type').in('id', uniq);
  return Object.fromEntries((data || []).map(s => [s.id, s.supplier_type]));
}

/**
 * Default supplier payable account by supplier type (for the payment voucher).
 */
export function payableAccountForSupplierType(supplierType) {
  switch ((supplierType || '').toLowerCase()) {
    case 'airline':           return ACCOUNTS.AIRLINE_PAYABLE;
    case 'hotel':
    case 'land operator':
    case 'land':
    case 'dmc':               return ACCOUNTS.HOTEL_LAND_PAYABLE;
    case 'visa':
    case 'visa agent':
    case 'embassy':           return ACCOUNTS.VISA_PAYABLE;
    default:                  return ACCOUNTS.AP_SUPPLIERS;
  }
}
